/**
 * Shared country-ISO resolvers used by card adapters
 * (`video-card-container`, `short-card-container`,
 * `series-card-container`, `episode-card-container`). Registers the
 * `i18n-iso-countries` English locale once at module load.
 */

import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import type { Video } from '@/types';

countries.registerLocale(enLocale);

/** Lowercase ISO 3166-1 alpha-2 code. Widened to `string` for ergonomics. */
export type IsoAlpha2 = string;

/**
 * Normalize a raw country string (ISO-2 or country name, any case) to
 * lowercase ISO 3166-1 alpha-2. Returns `undefined` for empty input
 * or unrecognized names.
 */
export function toIso(raw: string): IsoAlpha2 | undefined {
  const lower = raw.trim().toLowerCase();
  if (lower.length === 0) return undefined;
  if (/^[a-z]{2}$/.test(lower)) return lower;
  const code = countries.getAlpha2Code(raw.trim(), 'en');
  return code !== undefined && code !== '' ? code.toLowerCase() : undefined;
}

/**
 * Parse an ISO-2 from the tail of a "City, Country" location string.
 * Falls back to the whole string if no comma is present.
 */
export function parseLocationIso(location: string | null | undefined): IsoAlpha2 | undefined {
  if (location === null || location === undefined || location.length === 0) return undefined;
  const lastComma = location.lastIndexOf(',');
  const countryPart = lastComma >= 0 ? location.slice(lastComma + 1) : location;
  return toIso(countryPart);
}

/**
 * Resolve a Video's country ISO-2 by walking:
 * `country` (string or object) → `country.iso` → `country.name` →
 * `location` tail.
 */
export function resolveVideoCountryIso(video: Video): IsoAlpha2 | undefined {
  const c = video.country;
  if (c !== null && c !== undefined) {
    if (typeof c === 'string') {
      const fromStr = toIso(c);
      if (fromStr !== undefined) return fromStr;
    } else {
      if (c.iso !== undefined) {
        const fromIso = toIso(c.iso);
        if (fromIso !== undefined) return fromIso;
      }
      if (c.name !== undefined) {
        const fromName = toIso(c.name);
        if (fromName !== undefined) return fromName;
      }
    }
  }
  return parseLocationIso(video.location);
}

/** Lookup the English country name for an ISO-2 code (case-insensitive). */
export function countryName(code: IsoAlpha2): string {
  return countries.getName(code.toUpperCase(), 'en') ?? code.toUpperCase();
}
